import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface HotelStaticMapProps {
  latitude: number;
  longitude: number;
  hotelName: string;
  onClick: () => void;
}

export const HotelStaticMap = ({ latitude, longitude, hotelName, onClick }: HotelStaticMapProps) => {
  const { t } = useTranslation();
  const [mapUrl, setMapUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchMapUrl = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-mapbox-token');
        if (error) {
          console.error('Error from get-mapbox-token function:', error);
          throw error;
        }
        
        const token = data.token;
        // MapBox Static Images API
        const url = `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/pin-l+FF385C(${longitude},${latitude})/${longitude},${latitude},14,0/600x400@2x?access_token=${token}`;
        setMapUrl(url);
      } catch (error) {
        console.error('Error fetching map URL:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMapUrl();
  }, [latitude, longitude]);

  if (isLoading) {
    return <Skeleton className="w-full h-[400px] rounded-lg" />;
  }

  if (!mapUrl) {
    return (
      <div className="w-full h-[400px] rounded-lg bg-muted flex items-center justify-center">
        <p className="text-muted-foreground">{t('hotelDetail.mapLoadError')}</p>
      </div>
    );
  }

  return (
    <div 
      className="relative w-full h-[250px] rounded-lg overflow-hidden cursor-pointer group"
      onClick={onClick}
    >
      <img 
        src={mapUrl} 
        alt={`${t('hotelDetail.location')} - ${hotelName}`}
        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
      <div className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur-sm rounded-lg p-2.5 shadow-lg">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
          <div>
            <p className="font-semibold text-xs">{hotelName}</p>
            <p className="text-xs text-muted-foreground">
              {t('hotelDetail.clickToViewMap')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
